import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Stack, Typography } from "@mui/material";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

/**
 * Handoff card for what the `robot` specialist produced in chat: a JSON
 * Forms schema, or a snippet of robot code.
 *
 * Note on the form handoff: the Form Builder is per-robot
 * (/dashboard/robots/:id/form-builder) and the Assistant has no robot id --
 * the user described a form, not a robot. So the CTA opens the robot list,
 * where they pick the target, and the schema travels in `location.state`
 * plus the clipboard. Wiring the schema straight into a chosen robot needs a
 * robot picker that does not exist yet; this card is honest about that
 * instead of navigating somewhere that would silently drop the payload.
 */

interface RobotArtifactCardProps {
  schema?: Record<string, unknown>;
  uischema?: Record<string, unknown> | null;
  code?: string;
  language?: string;
}

const RobotArtifactCard: React.FC<RobotArtifactCardProps> = ({
  schema,
  uischema,
  code,
  language,
}) => {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const isForm = Boolean(schema);
  const payload = isForm
    ? JSON.stringify(uischema ? { schema, uischema } : { schema }, null, 2)
    : code ?? "";

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(payload);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable outside a secure context -- stay quiet.
    }
  };

  const fieldCount = isForm
    ? Object.values(schema ?? {}).reduce((total: number, section: any) => {
        const properties = section?.properties;
        return total + (properties ? Object.keys(properties).length : 0);
      }, 0)
    : 0;

  return (
    <Box
      sx={{
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        p: 1.5,
        my: 0.5,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <SmartToyOutlinedIcon fontSize="small" color="primary" />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {isForm ? "Schema della form pronto" : "Codice del robot pronto"}
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {isForm
          ? `${Object.keys(schema ?? {}).length} sezione/i · ${fieldCount} campo/i`
          : `${language || "text"} · ${payload.split("\n").length} righe`}
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
        {isForm && (
          <Button
            size="small"
            variant="contained"
            sx={{ textTransform: "none" }}
            onClick={() =>
              navigate("/dashboard/robots", {
                state: { robotSchema: schema, robotUiSchema: uischema },
              })
            }
          >
            Scegli il robot
          </Button>
        )}
        <Button
          size="small"
          startIcon={<ContentCopyIcon fontSize="small" />}
          sx={{ textTransform: "none" }}
          onClick={handleCopy}
        >
          {copied ? "Copiato" : isForm ? "Copia JSON" : "Copia codice"}
        </Button>
      </Stack>
    </Box>
  );
};

export default RobotArtifactCard;
